/**
 * V2/V3 → V11 adapter for the Billing Module.
 *
 * The listing endpoint moved from `/billing/v11/clinic/monthly` to
 * `/pending-tasks-v3`, and the response shape changed: instead of
 * `{ data: { list_of_data: BillingRow[] } }` the backend now returns a
 * paginated envelope of `V3PatientRow` objects (see
 * `src/hexagon/core/ports/BillingModule.d.ts`).
 *
 * The whole page (tabs, enrichment, CSV/PDF exports, MonthlyReportModal)
 * still consumes the V11 `BillingRow` shape — this adapter bridges the two
 * by producing a `BillingRow`-shaped object from each V2/V3 row.
 *
 * Two exports:
 *
 *   1. **`fromV2Only(v2Row)`** — direct field mapping. V2 carries most of
 *      what the UI reads: DOB, gender, consent file, signature record, full
 *      sign-off block with user details + text notes + PDF S3 key, order
 *      block with `message` + `created_by_user`. CPT flags stay zeroed.
 *
 *   2. **`fromV2ForAdminExport(v2Row)`** — `fromV2Only` plus CPT flags
 *      reconstructed from the posted `charges[]`: the canonical RPM four
 *      the CSV/report views read, plus flexible `cpt_<code>` /
 *      `cpt_<code>_count` keys for every other billed code. This is the
 *      mapping the service uses for the listing.
 *
 * Fields that remain lossy in both paths (they only existed on the V11
 * sign-off snapshot): `no_of_days_with_recording`,
 * `total_time_spent_reviewing*`, and the `cpt_*_dos` arrays. Those still
 * come from `getSignOffAssessment` for signed rows.
 */

import type {
  BillingClinicUser,
  BillingRow,
  ClinicPatientSignOffBilling,
  ConsentStatus,
  DigitalSignatureRecord,
  PccDiagnosisEntry,
  V2Charge,
  V2DigitalSignatureRecord,
  V2PatientRow,
  V2SignOffBlock,
  V2UserDetails,
} from "../hexagon/core/ports/BillingModule";

// ─── V2 user details → V11 BillingClinicUser ────────────────────────────────

/**
 * Widen a V2 user-details record into V11's open-ended `BillingClinicUser`
 * shape. Carries the same observable fields the modal reads (name, email,
 * NPI from licenses[0] when present); unknown V11 fields stay undefined
 * which the modal already handles.
 */
const widenUserDetails = (
  user: V2UserDetails | null | undefined
): BillingClinicUser | null => {
  if (!user) return null;
  const primaryLicense = user.licenses?.[0];
  const widened: BillingClinicUser = {
    id: user.id,
    first_name: user.first_name ?? null,
    last_name: user.last_name ?? null,
    title: user.title?.trim() || user.user_type || null,
    email: user.email,
    // Preserve full license list for code that reads beyond [0].
    licenses: user.licenses ?? [],
  };
  if (primaryLicense?.npi) {
    widened.npi = primaryLicense.npi;
  }
  return widened;
};

// ─── V2 sign_off → V11 ClinicPatientSignOffBilling ──────────────────────────

/**
 * Build the inner `ClinicPatientSignOffBilling` from V2's `sign_off` block.
 *
 * V2 hoists per-step text notes and the PDF S3 key, so we get a true
 * 1:1 mapping with V11's wrapper (no defaults required for these fields).
 * Fields V2 still doesn't carry (`status`, `created_at`, `updated_at`) get
 * sensible defaults from adjacent timestamps.
 */
const buildSignOffInner = (
  v2Row: V2PatientRow
): ClinicPatientSignOffBilling | null => {
  const so = v2Row.sign_off;
  if (!so || so.sign_off_id === null) return null;
  return {
    id: so.sign_off_id,
    patient_id: v2Row.patient_id,
    billing_date: so.billing_date,
    status: 1,
    created_at: so.document_datetime ?? "",
    updated_at:
      so.collected_datetime ??
      so.billed_datetime ??
      so.sign_off_datetime ??
      so.document_datetime ??
      "",

    // V11 step fields — V2 hoists each one out of the inner wrapper.
    sign_off_user: so.sign_off_user,
    sign_off_datetime: so.sign_off_datetime,
    sign_off_document: so.sign_off_document,

    billed_user: so.billed_user,
    billed_datetime: so.billed_datetime,
    billed_text: so.billed_text,

    collected_user: so.collected_user,
    collected_datetime: so.collected_datetime,
    collected_amount: so.collected_amount,

    document_user: so.document_user,
    document_datetime: so.document_datetime,
    document_text: so.document_text,
  };
};

// ─── V2 digital_signature_record → V11 DigitalSignatureRecord ───────────────
//
// The backend returns one of two shapes here:
//
//   1. **Full V11 shape** (current production reality): the record is
//      already a `DigitalSignatureRecord` — camelCase `patientName`,
//      `patientMRNumber`, `facilityRepSignatureFileName`,
//      `reasonForNoSignature`, `formsProvidedDatetime`, etc. This carries
//      everything the modal renders (incl. the facility-signed branch) and
//      must be passed through *untouched*.
//
//   2. **Slim V2 audit shape**: only `signed_at` / `signer_name` / `ip` /
//      `signature_image_path`. Widened into the V11 shape with the audit
//      fields mapped across; representation fields stay undefined.
//
// We detect (1) by the presence of any V11-only field and pass it straight
// through; otherwise we widen the slim audit record. Mapping the slim
// fields blindly would silently drop every field of a full V11 record
// because none of `signed_at`/`signer_name`/`signature_image_path` exist
// on it.

const isLegacySignatureRecord = (
  src: V2DigitalSignatureRecord | DigitalSignatureRecord
): src is DigitalSignatureRecord => {
  const r = src as DigitalSignatureRecord;
  return (
    r.patientName !== undefined ||
    r.patientMRNumber !== undefined ||
    r.patientOrRepName !== undefined ||
    r.patientRepRelation !== undefined ||
    r.patientRepSignatureFileName !== undefined ||
    r.facilityName !== undefined ||
    r.facilityRepSignatureFileName !== undefined ||
    r.reasonForNoSignature !== undefined ||
    r.formsProvidedDatetime !== undefined
  );
};

const widenSignatureRecord = (
  src: V2DigitalSignatureRecord | DigitalSignatureRecord | null | undefined
): DigitalSignatureRecord | null => {
  if (!src) return null;
  // Already a full V11-shaped record — pass through verbatim.
  if (isLegacySignatureRecord(src)) return src;
  // Slim V2 audit record — widen the audit fields into the V11 shape.
  return {
    date: src.signed_at,
    patientName: src.signer_name ?? undefined,
    patientRepSignatureFileName: src.signature_image_path ?? undefined,
  };
};

// ─── V2 diagnosis → V11 BillingRow.diagnosis ────────────────────────────────
//
// V2 already returns the V11 `DiagnosisEntry` shape parsed
// (`{ rank, icd10, conditionId?, icd10Description }`). V11's `BillingRow`
// stores the field as a JSON string for legacy compatibility — we just
// re-stringify the parsed array as-is, no shape translation.

const v2DiagnosisToV11Json = (
  src: V2PatientRow["diagnosis"]
): string | null => {
  if (!src || !src.length) return null;
  return JSON.stringify(src);
};

// ─── V2 pcc_diagnosis → V11 PccDiagnosisEntry[] ─────────────────────────────
//
// V2 returns the parsed array directly; V11 expects a parsed array (not a
// JSON string) too — easy widen.

const v2PccDiagnosisToV11 = (
  src: V2PatientRow["pcc_diagnosis"]
): PccDiagnosisEntry[] | null => {
  if (!src || !src.length) return null;
  return src.map((d, index) => {
    // The backend returns one of two field conventions (see
    // `V2PccDiagnosisEntry`): wide V11-style (`icd10`/`icd10Description`/
    // `rankDescription`) or slim V2 audit (`code`/`description`). Read
    // whichever is present.
    const icd10 = d.icd10 ?? d.code ?? "";
    const icd10Description = d.icd10Description ?? d.description ?? "";
    // Prefer the source rank; fall back to "Primary" for the first entry so
    // the modal's `rankDescription === "Primary"` checks still work.
    const rankDescription = d.rankDescription ?? (index === 0 ? "Primary" : "");
    return {
      icd10,
      icd10Description,
      rankDescription,
      principalDiagnosis: rankDescription === "Primary",
      clinicalStatus: d.clinicalStatus ?? undefined,
    };
  });
};

// ─── V2-only mapping (rich, near-1:1 with V11) ──────────────────────────────

/**
 * Build a `BillingRow`-shaped object from V2 alone.
 *
 * Lossy fields (kept as `false` / `0` / `null`):
 *   - `cpt_99453/4/7/8` eligibility flags
 *   - `cpt_99458_count`, `cpt_99458_dos[]`
 *   - `no_of_days_with_recording`
 *   - `total_time_spent_reviewing` / `_for_99458`
 *
 * Everything else carries through from V2 — including DOB, gender, the
 * full sign-off block with user details and text notes, the order block,
 * and the signature record.
 */
export const fromV2Only = (
  v2Row: V2PatientRow
): Omit<V2PatientRow, "payer_information_json" | "extra_info" | "diagnosis"> &
  BillingRow => {
  const signOffInner = buildSignOffInner(v2Row);
  const so: V2SignOffBlock | null = v2Row.sign_off;
  return {
    // ─── All V2 fields, for slow migration ───
    ...v2Row,

    // ─── Identity ───
    id: v2Row.patient_id,
    first_name: v2Row.first_name,
    last_name: v2Row.last_name,
    gender: v2Row.gender,
    dob: v2Row.dob,

    date_of_admission: v2Row.date_of_admission,

    // ─── Consent ───
    consent_status: (v2Row.consented_status ?? 2) as ConsentStatus,
    consent_file: v2Row.consent_file,
    consented_datetime: v2Row.consented_datetime,
    digital_signature_record: widenSignatureRecord(
      v2Row.digital_signature_record
    ),
    information_file: v2Row.information_file,

    // ─── JSON-string fields the modal still reads via JSON.parse() ───
    diagnosis: v2DiagnosisToV11Json(v2Row.diagnosis),
    payer_information_json: v2Row.payer_information_json
      ? JSON.stringify(v2Row.payer_information_json)
      : null,
    extra_info: v2Row.extra_info ? JSON.stringify(v2Row.extra_info) : null,

    // ─── Sign-off + user-step blocks (V2 carries full user objects) ───
    billing_status_data: signOffInner
      ? {
          ClinicPatientSignOffBilling: signOffInner,
          sign_off_user: widenUserDetails(so?.sign_off_user_details),
          billed_user: widenUserDetails(so?.billed_user_details),
          collected_user: widenUserDetails(so?.collected_user_details),
          document_user: widenUserDetails(so?.document_user_details),
        }
      : null,

    // ─── Care plan / order ───
    care_plan: v2Row.order?.care_plan_id
      ? {
          ClinicPatientCarePlan: {
            id: v2Row.order.care_plan_id,
            patient_id: v2Row.patient_id,
            message: v2Row.order.message ?? "",
            // V11 stored old_message as a single string blob; V2 returns
            // structured edit history. Stringify the most recent prior
            // text so the modal's diff/history UI still has something
            // to render.
            old_message:
              Array.isArray(v2Row.order.old_message) &&
              v2Row.order.old_message.length
                ? v2Row.order.old_message[v2Row.order.old_message.length - 1]
                    ?.previous_text ?? null
                : null,
            valid_till_date: v2Row.order.valid_till_date,
            created_by: v2Row.order.created_by_user?.id ?? 0,
            created_at: v2Row.order.last_order_date ?? "",
            updated_at: v2Row.order.last_order_date ?? "",
            status: 1,
          },
          User: widenUserDetails(v2Row.order.created_by_user) ?? undefined,
          LicenseInfo: v2Row.order.created_by_user?.licenses ?? [],
        }
      : ({} as Record<string, never>),
    disable_add_care_plan: false,

    // ─── CPT eligibility — derived from charges in fromV2ForAdminExport ───
    cpt_99453: false,
    cpt_99454: false,
    cpt_99457: false,
    cpt_99458: false,
    cpt_99458_dos: [],
    cpt_99458_count: 0,
    no_of_days_with_recording: 0,

    // ─── PCC payload ───
    pcc_diagnosis: v2PccDiagnosisToV11(v2Row.pcc_diagnosis),
    billing_information: v2Row.billing_information,
    cp_billing_information: v2Row.cp_billing_information ?? "",
  };
};

// ─── Charge-derived CPT flags ────────────────────────────────────────────────

/**
 * Canonical RPM CPT flags the CSV/metrics builders read off a
 * `BillingRow`. The dollar amounts keyed off these flags are the fixed
 * manual rates in `billingCsv.CPT_DOLLAR_AMOUNTS` — never the payer-paid
 * amounts on the charge.
 */
type CanonicalCptFlag = "cpt_99453" | "cpt_99454" | "cpt_99457" | "cpt_99458";

/**
 * Reverse of `billingCpt.CPT_CODES_BY_BILLING_TYPE`: maps every billed CPT
 * variant back to its canonical RPM flag. There are no collisions across the
 * three billing types (98975→99453, 98976/98977→99454, 98980→99457,
 * 98981→99458), so we can resolve a charge's canonical bucket from the code
 * alone — no per-facility `billing_type` lookup required.
 *
 * CPT codes absent from this map (e.g. G0237, 99091) still get flexible
 * `cpt_<code>` / `cpt_<code>_count` keys on the derived flags — they just
 * don't fold into one of the four canonical RPM buckets, so the RPM dollar
 * columns ignore them.
 */
const CHARGE_CPT_TO_CANONICAL: Readonly<Record<string, CanonicalCptFlag>> = {
  "99453": "cpt_99453",
  "98975": "cpt_99453",
  "99454": "cpt_99454",
  "98976": "cpt_99454",
  "98977": "cpt_99454",
  "99457": "cpt_99457",
  "98980": "cpt_99457",
  "99458": "cpt_99458",
  "98981": "cpt_99458",
};

/**
 * CPT flags derived from `charges[]`: the canonical RPM keys the CSV/metrics
 * builders read off a `BillingRow` (strongly typed), plus a flexible
 * `cpt_<code>` presence flag and `cpt_<code>_count` unit total for every
 * OTHER CPT code the backend returns — whatever string it is.
 */
type DerivedCptFlags = Record<`cpt_${string}`, boolean | number> &
  Record<`cpt_${string}_count`, number>;

/**
 * Derive CPT presence flags + unit counts from a patient's V2 `charges[]`.
 *
 * Business rule (confirmed with product, Jun 2026):
 *   - A CPT is "present" for the month when the patient has at least one
 *     NON-VOIDED charge with that code. (`voided` charges are excluded;
 *     every other lifecycle status counts — new / draft / ready / pushed /
 *     submitted / backlog.)
 *   - Codes in `CHARGE_CPT_TO_CANONICAL` fold into their canonical RPM
 *     bucket (e.g. 98981 sets `cpt_99458`); any other code gets a dynamic
 *     `cpt_<code>` flag so nothing the backend bills is dropped.
 *   - Each `cpt_<code>_count` = total billed units across that code's
 *     non-voided charges (a charge contributes at least 1). The downstream
 *     dollar calc caps `cpt_99458_count` at 2
 *     (`billingCsv.cpt99458DollarAmount`).
 *   - Dollar VALUES are the fixed manual rates downstream — the charge's
 *     `pr_paid`/`se_paid`/`tr_paid` are never read here.
 */
const deriveCptFlagsFromCharges = (
  charges: V2Charge[] | null | undefined
): DerivedCptFlags => {
  const flags: DerivedCptFlags = {
    cpt_99453: false,
    cpt_99454: false,
    cpt_99457: false,
    cpt_99458: false,
    cpt_99458_count: 0,
  };
  if (!Array.isArray(charges)) return flags;
  for (const charge of charges) {
    if (charge.status === "voided") continue;
    const flagKey: `cpt_${string}` =
      CHARGE_CPT_TO_CANONICAL[charge.cpt] ?? `cpt_${charge.cpt}`;
    flags[flagKey] = true;
    const countKey: `cpt_${string}` = `${flagKey}_count`;
    const previous = flags[countKey];
    flags[countKey] =
      (typeof previous === "number" ? previous : 0) +
      (charge.units > 0 ? charge.units : 1);
  }
  return flags;
};

/**
 * Build a `BillingRow` from a V2/V3 pending-tasks row with the CPT
 * eligibility flags — absent from the pending-tasks payload — reconstructed
 * from the posted `charges[]` (see `deriveCptFlagsFromCharges`). Every
 * billed CPT surfaces as a `cpt_<code>` / `cpt_<code>_count` pair; the
 * canonical RPM four keep their strongly-typed `BillingRow` fields.
 *
 * This is the mapping the billing service uses for the monthly listing so
 * the CPT columns, dollar CSVs, and metrics builders keep working with the
 * fixed manual CPT dollar rates unchanged.
 *
 * (Name kept in sync with the `billingNewDocumentation` branch, where this
 * mapping was introduced for the admin multi-facility export path.)
 *
 * `no_of_days_with_recording` stays 0 (not in the pending-tasks payload).
 * The export's dollar gate (`isBillableForDollarCsv`) only requires one of
 * 99454/99457/99458, so the 99453-only-with-recording branch never affects
 * the dollar totals.
 */
export const fromV2ForAdminExport = (v2Row: V2PatientRow): BillingRow => ({
  ...fromV2Only(v2Row),
  ...deriveCptFlagsFromCharges(v2Row.charges),
});
